import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Store, Plus, Check, X, DollarSign, TrendingUp, Package,
  Settings, RefreshCw, Zap, Bell, BellOff, Pause, Play,
  Edit3, ChevronDown, ChevronUp, Filter, Search, BarChart3,
  Truck, ShoppingBag, Wallet, PiggyBank, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Fee breakdown component
const FeeBreakdown = ({ fees }) => (
  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
    <div className="flex justify-between">
      <span className="text-gray-600">Base Price</span>
      <span className="font-medium">${fees?.base_price?.toFixed(2)}</span>
    </div>
    <div className="flex justify-between text-blue-600">
      <span>+ Delivery (5.3%)</span>
      <span>${fees?.delivery_markup?.toFixed(2)}</span>
    </div>
    <div className="flex justify-between text-green-600">
      <span>+ Sales Tax (7%)</span>
      <span>${fees?.sales_tax?.toFixed(2)}</span>
    </div>
    <div className="flex justify-between text-purple-600">
      <span>+ Processing (1.8%)</span>
      <span>${fees?.processing_fee?.toFixed(2)}</span>
    </div>
    <div className="flex justify-between text-orange-600">
      <span>+ Margin Buffer (0.7%)</span>
      <span>${fees?.margin_buffer?.toFixed(2)}</span>
    </div>
    <div className="border-t pt-2 flex justify-between font-bold">
      <span>Final Price</span>
      <span>${fees?.final_price?.toFixed(2)}</span>
    </div>
    <div className="flex justify-between text-green-700 font-medium">
      <span>Your Earnings (14.8%)</span>
      <span>${fees?.owner_earnings?.toFixed(2)}</span>
    </div>
  </div>
);

// Ad Card Component
const AdCard = ({ ad, onApprove, onReject, onUpdatePrice, isProcessing }) => {
  const [expanded, setExpanded] = useState(false);
  const [editPrice, setEditPrice] = useState(false);
  const [newPrice, setNewPrice] = useState(ad.base_price);
  
  const targetColors = {
    food: 'bg-orange-100 text-orange-800',
    auto_parts: 'bg-blue-100 text-blue-800',
    marketplace: 'bg-purple-100 text-purple-800'
  };
  const targetLabel = ad.ad_target === 'food' ? 'Food Delivery' : ad.ad_target === 'auto_parts' ? 'Auto Parts' : 'Marketplace';
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {ad.images?.[0] ? (
              <img src={ad.images[0]} alt={ad.title} className="w-12 h-12 rounded-lg object-cover" />
            ) : (
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-gray-900 line-clamp-1">{ad.title}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-sm text-gray-500 capitalize">{ad.category?.replace('_', ' ')}</p>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${targetColors[ad.ad_target] || targetColors.marketplace}`}>
                  {targetLabel}
                </span>
                {ad.restaurant_name && <span className="text-xs text-orange-600">{ad.restaurant_name}</span>}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-lg font-bold text-gray-900">${ad.final_price?.toFixed(2)}</p>
              <p className="text-xs text-green-600">+${ad.fees?.owner_earnings?.toFixed(2)} earnings</p>
            </div>
            
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              ad.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
              ad.status === 'published' ? 'bg-green-100 text-green-800' :
              'bg-red-100 text-red-800'
            }`}>
              {ad.status?.replace('_', ' ')}
            </span>
            
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-4">
          <p className="text-gray-600 text-sm">{ad.description}</p>
          
          <FeeBreakdown fees={ad.fees} />
          
          {ad.status === 'pending_review' && (
            <div className="flex flex-wrap gap-3">
              {!editPrice ? (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); onApprove(ad.ad_id); }}
                    disabled={isProcessing}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" /> Approve & Publish
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onReject(ad.ad_id); }}
                    disabled={isProcessing}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    <X className="w-4 h-4" /> Reject
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditPrice(true); }}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Edit3 className="w-4 h-4" /> Edit Price
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-3 w-full">
                  <span className="text-gray-600">Base Price: $</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newPrice}
                    onChange={(e) => setNewPrice(parseFloat(e.target.value))}
                    onClick={(e) => e.stopPropagation()}
                    className="w-24 px-3 py-2 border rounded-lg"
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); onUpdatePrice(ad.ad_id, newPrice); setEditPrice(false); }}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Update
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditPrice(false); }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
export default function AIMarketplaceDashboard() {
  const [activeTab, setActiveTab] = useState('ads');
  const [pendingAds, setPendingAds] = useState([]);
  const [earnings, setEarnings] = useState({ buckets: [], total_balance: 0, recent_sales: [] });
  const [feeStructure, setFeeStructure] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [aiPricingStatus, setAiPricingStatus] = useState({ ai_pricing_enabled: true });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Generate form state
  const [generateForm, setGenerateForm] = useState({
    count: 10,
    category: 'auto_parts',
    use_ai_optimization: true
  });
  
  const fetchData = useCallback(async () => {
    try {
      const [adsRes, earningsRes, feeRes, notifRes, statusRes] = await Promise.all([
        axios.get(`${API}/api/ai-marketplace/pending`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/ai-marketplace/owner-earnings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/ai-marketplace/fee-structure`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/ai-marketplace/notification-settings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/ai-marketplace/ai-pricing-status`, { headers: getAuthHeaders() })
      ]);
      
      setPendingAds(adsRes.data.ads || []);
      setEarnings(earningsRes.data);
      setFeeStructure(feeRes.data);
      setNotificationSettings(notifRes.data);
      setAiPricingStatus(statusRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleGenerateAds = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/ai-marketplace/generate`, generateForm, { headers: getAuthHeaders() });
      toast.success(`Generated ${res.data.ads_generated} ads!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate ads');
    } finally {
      setGenerating(false);
    }
  };
  
  const handleGenerateFoodAds = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/ai-marketplace/generate-food-ads`, {
        restaurant_id: 'mcdonalds_fw_001',
        count: 10,
        use_ai_optimization: true
      }, { headers: getAuthHeaders() });
      toast.success(`Generated ${res.data.ads_generated} food ads from ${res.data.restaurant}!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate food ads. Make sure restaurant data is scraped first.');
    } finally {
      setGenerating(false);
    }
  };
  
  const handleApprove = async (adId) => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/api/ai-marketplace/approve/${adId}`, {}, { headers: getAuthHeaders() });
      const dest = res.data.destination === 'food' ? 'Food Delivery' : res.data.destination === 'auto_parts' ? 'Auto Parts' : 'Marketplace';
      toast.success(`Ad approved and sent to ${dest}!`);
      fetchData();
    } catch (error) {
      toast.error('Failed to approve ad');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleReject = async (adId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/api/ai-marketplace/reject/${adId}`, {}, { headers: getAuthHeaders() });
      toast.success('Ad rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject ad');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleUpdatePrice = async (adId, newPrice) => {
    setProcessing(true);
    try {
      await axios.put(`${API}/api/ai-marketplace/price/${adId}`, { base_price: newPrice }, { headers: getAuthHeaders() });
      toast.success('Price updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update price');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleApproveAll = async () => {
    if (!confirm('Approve and publish all pending ads?')) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/api/ai-marketplace/approve-all`, {}, { headers: getAuthHeaders() });
      toast.success(`Approved ${res.data.approved} ads!`);
      fetchData();
    } catch (error) {
      toast.error('Failed to bulk approve');
    } finally {
      setProcessing(false);
    }
  };
  
  const toggleAiPricing = async () => {
    try {
      if (aiPricingStatus.ai_pricing_enabled) {
        await axios.post(`${API}/api/ai-marketplace/halt-ai-pricing`, {}, { headers: getAuthHeaders() });
        toast.success('AI pricing halted');
      } else {
        await axios.post(`${API}/api/ai-marketplace/resume-ai-pricing`, {}, { headers: getAuthHeaders() });
        toast.success('AI pricing resumed');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle AI pricing');
    }
  };
  
  const toggleMuteAll = async () => {
    try {
      const newMute = !notificationSettings?.mute_all;
      await axios.post(`${API}/api/ai-marketplace/mute-all`, { mute: newMute }, { headers: getAuthHeaders() });
      toast.success(newMute ? 'Notifications muted' : 'Notifications enabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle notifications');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="ai-marketplace-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                <Store className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">AI Marketplace</h1>
                <p className="text-gray-500">Generate, manage & sell with AI</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              {/* AI Pricing Toggle */}
              <button
                onClick={toggleAiPricing}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  aiPricingStatus.ai_pricing_enabled 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                {aiPricingStatus.ai_pricing_enabled ? (
                  <><Play className="w-4 h-4" /> AI Active</>
                ) : (
                  <><Pause className="w-4 h-4" /> AI Halted</>
                )}
              </button>
              
              {/* Mute Toggle */}
              <button
                onClick={toggleMuteAll}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  notificationSettings?.mute_all 
                    ? 'bg-gray-200 text-gray-600' 
                    : 'bg-blue-100 text-blue-700'
                }`}
              >
                {notificationSettings?.mute_all ? (
                  <><BellOff className="w-4 h-4" /> Muted</>
                ) : (
                  <><Bell className="w-4 h-4" /> Notifications</>
                )}
              </button>
              
              <button
                onClick={fetchData}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Ads</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingAds.length}</p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Earnings</p>
                <p className="text-2xl font-bold text-green-600">${earnings.total_balance?.toFixed(2)}</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Fee Rate</p>
                <p className="text-2xl font-bold text-purple-600">14.8%</p>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Recent Sales</p>
                <p className="text-2xl font-bold text-blue-600">{earnings.recent_sales?.length || 0}</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {['ads', 'generate', 'earnings', 'fees'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                activeTab === tab 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50 border'
              }`}
            >
              {tab === 'ads' && 'Pending Ads'}
              {tab === 'generate' && 'Generate Ads'}
              {tab === 'earnings' && 'Earnings'}
              {tab === 'fees' && 'Fee Structure'}
            </button>
          ))}
        </div>
        
        {/* Pending Ads Tab */}
        {activeTab === 'ads' && (
          <div className="space-y-4">
            {pendingAds.length > 0 && (
              <div className="flex justify-end">
                <button
                  onClick={handleApproveAll}
                  disabled={processing}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <Check className="w-4 h-4" /> Approve All ({pendingAds.length})
                </button>
              </div>
            )}
            
            {pendingAds.length > 0 ? (
              pendingAds.map(ad => (
                <AdCard
                  key={ad.ad_id}
                  ad={ad}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onUpdatePrice={handleUpdatePrice}
                  isProcessing={processing}
                />
              ))
            ) : (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Pending Ads</h3>
                <p className="text-gray-500 mb-4">Generate new AI-powered marketplace listings</p>
                <button
                  onClick={() => setActiveTab('generate')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Generate Ads
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* Generate Tab */}
        {activeTab === 'generate' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              AI Ad Generator
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block font-medium text-gray-900 mb-2">Number of Ads</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={generateForm.count}
                  onChange={(e) => setGenerateForm({...generateForm, count: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">Generate 1-1000 ads</p>
              </div>
              
              <div>
                <label className="block font-medium text-gray-900 mb-2">Category</label>
                <select
                  value={generateForm.category}
                  onChange={(e) => setGenerateForm({...generateForm, category: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="auto_parts">Auto Parts</option>
                  <option value="tools">Tools</option>
                  <option value="electronics">Electronics</option>
                  <option value="home_garden">Home & Garden</option>
                </select>
              </div>
              
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={generateForm.use_ai_optimization}
                    onChange={(e) => setGenerateForm({...generateForm, use_ai_optimization: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
                <span className="font-medium text-gray-900">Use AI Optimization</span>
              </div>
            </div>
            
            <button
              onClick={handleGenerateAds}
              disabled={generating}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 font-medium"
              data-testid="generate-marketplace-ads-btn"
            >
              {generating ? (
                <>Generating...</>
              ) : (
                <><Plus className="w-5 h-5" /> Generate {generateForm.count} Marketplace Ads</>
              )}
            </button>
            
            {/* Food Ad Generator - separate section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="text-orange-500">🍔</span> Food Delivery Ad Generator
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Generate food ads from <strong>real scraped restaurant menus</strong> with real photos. 
                Approved food ads go directly to the Food Delivery section (not general marketplace).
              </p>
              <button
                onClick={handleGenerateFoodAds}
                disabled={generating}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 disabled:opacity-50 font-medium"
                data-testid="generate-food-ads-btn"
              >
                {generating ? (
                  <>Generating Food Ads...</>
                ) : (
                  <><Plus className="w-5 h-5" /> Generate Food Ads from Real Restaurant Data</>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Earnings Tab */}
        {activeTab === 'earnings' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {earnings.buckets?.map(bucket => (
                <div key={bucket.bucket} className="bg-white rounded-xl border p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <PiggyBank className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-gray-900 capitalize">
                      {bucket.bucket?.replace('owner_', '').replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">${bucket.balance?.toFixed(2)}</p>
                </div>
              ))}
            </div>
            
            <div className="bg-white rounded-xl border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Sales</h3>
              {earnings.recent_sales?.length > 0 ? (
                <div className="space-y-3">
                  {earnings.recent_sales.map(sale => (
                    <div key={sale.sale_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{sale.product_name}</p>
                        <p className="text-sm text-gray-500">{new Date(sale.created_at).toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">+${sale.total_deposited?.toFixed(2)}</p>
                        <p className="text-sm text-gray-500">Sale: ${sale.sale_price?.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No sales yet</p>
              )}
            </div>
          </div>
        )}
        
        {/* Fee Structure Tab */}
        {activeTab === 'fees' && feeStructure && (
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Fee Structure (14.8% Total)</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {Object.entries(feeStructure.fees || {}).map(([key, fee]) => (
                  <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 capitalize">{key.replace('_', ' ')}</p>
                      <p className="text-sm text-gray-500">{fee.description}</p>
                    </div>
                    <span className="text-lg font-bold text-blue-600">{Number(fee.percent).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Example: $100 Product</h3>
                <FeeBreakdown fees={feeStructure.example} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
